<script setup lang="ts">
import { computed, onMounted, type Ref, ref, watch, provide } from 'vue'
import PChart from 'primevue/chart'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Chart, type ChartOptions } from 'chart.js'
import { storeToRefs } from 'pinia'

import { useNabooContent } from '@/assets/composables/useNabooContent.ts'
import { useDashboardClassroomResultStore } from '@/presentation/stores/dashboard/dashboardClassroomResult.ts'
import PProgressSpinner from 'primevue/progressspinner'
import PMessage from 'primevue/message'
import PSelectButton from 'primevue/selectbutton'
import PButton from 'primevue/button'
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'
import Values = QuizType.Values
import CDashboardClassroomResultsDiagnosticTable from '@/presentation/components/dashboard/CDashboardClassroomResultsDiagnosticTable.vue'
import type { ClassroomDiagnosticStudentResult } from '@/core/domain/dashboard/ClassroomDiagnosticStudentResult'
import {
  countEvaluationsByRange,
  EVALUATION_COLORS,
  type EvaluationStats
} from '@/presentation/utils/dashboardResultsUtils'
import { useDashboardClassroomStore } from '@/presentation/stores/dashboard/dashboardClassroom.ts'
import type { QuizVersion } from '@/core/domain/quiz/QuizVersion.ts'

// Chart data interface for diagnostic results
interface DiagnosticChartData {
  labels: string[]
  datasets: {
    label: 'Défi' | 'Efforts à faire' | 'Force'
    backgroundColor: string
    data: number[]
  }[]
}

// Référencer les plugins de Chart.js
Chart.register(ChartDataLabels)

// Stores
const dashboardClassroomStore = useDashboardClassroomStore()
const dashboardClassroomResultStore = useDashboardClassroomResultStore()

// Store Refs
const { dashboardClassroomDiagnosticsByType } = storeToRefs(dashboardClassroomStore)
const { dashboardClassroomResultStoreLoading,  } = storeToRefs(
  dashboardClassroomResultStore
)

// Variables, props
const props = defineProps<{
  schoolId: number
  classroomId: number
  diagnosticResults: ClassroomDiagnosticStudentResult[]
}>()

// Local state
const loading = ref(false)
const error = ref<string | null>(null)
const dataInitialized = ref(false)

type DiagnosticOption = {
  name: string
  value: QuizType.Values.DIAG_START | QuizType.Values.DIAG_END
}

const diagnosticOptions: DiagnosticOption[] = [
  {
    name: QuizType.label(QuizType.Values.DIAG_START),
    value: QuizType.Values.DIAG_START
  },
  {
    name: QuizType.label(QuizType.Values.DIAG_END),
    value: QuizType.Values.DIAG_END
  }
]
const diagnosticModel: Ref<QuizType.Values.DIAG_START|QuizType.Values.DIAG_END> = ref(diagnosticOptions[0].value)

// Composables
const { openQuizVersionDialog } = useNabooContent()

/**
 * Initialise le composant avec les données reçues via props
 */
const loadDiagnosticData = async () => {
  // Les données sont maintenant reçues via props, pas besoin de les charger
  dataInitialized.value = true
}

const openDiagnostic = () => {
  const title = QuizType.label(diagnosticModel.value)
  const blocks = Object.fromEntries(dashboardClassroomDiagnosticsByType.value[diagnosticModel.value]
    .map((qv, index) => [`Partie ${index+1}`, qv])) as Record<string, QuizVersion>
  openQuizVersionDialog(title, blocks)
}

onMounted(async () => {
  await loadDiagnosticData()
})

// Provide auth info to child components to avoid recreating auth store
// Note: We'll need to get this from a parent component or create a composable
provide('authInfo', { isAdmin: false, isEduEngineer: false })

// Observer les changements de props pour réinitialiser l'état
watch([() => props.schoolId, () => props.classroomId, () => props.diagnosticResults], () => {
  // Réinitialiser quand les props changent
  dataInitialized.value = true
})

// Data source with teacher full names for easier filtering
const dataSource = computed(() => {
  return props.diagnosticResults.map((student) => ({
    ...student,
    teacherFullname:
      student.teacherFirstname && student.teacherLastname
        ? `${student.teacherFirstname} ${student.teacherLastname}`
        : undefined
  }))
})

// Extract unique teachers for filter options
const teachersOptions = computed(() => {
  const uniqueTeachers = [
    ...new Set(
      dataSource.value
        .filter((student) => student.teacherFullname)
        .map((student) => student.teacherFullname)
    )
  ]

  const teacherOptions = uniqueTeachers
    .sort()
    .map((teacher) => ({ label: teacher, value: teacher }))

  return [{ label: 'Tous les enseignants', value: null }, ...teacherOptions]
})

// Transform diagnostic data for chart display (simplified)
const transformDataForChart = computed((): DiagnosticChartData => {
  const data = dataSource.value

  // Initialize chart structure
  const chartData: DiagnosticChartData = {
    labels: [],
    datasets: [
      {
        label: 'Défi',
        backgroundColor: EVALUATION_COLORS.DEFI,
        data: []
      },
      {
        label: 'Efforts à faire',
        backgroundColor: EVALUATION_COLORS.EFFORT_A_FAIRE,
        data: []
      },
      {
        label: 'Force',
        backgroundColor: EVALUATION_COLORS.FORCE,
        data: []
      }
    ]
  }

  if (data.length === 0) return chartData

  // Group tasks by taskId and count evaluations
  const taskStatsMap = new Map<number, EvaluationStats>()

  data.forEach((student) => {
    student.tasks?.forEach((task) => {
      if (!taskStatsMap.has(task.taskId)) {
        taskStatsMap.set(task.taskId, {
          notStarted: 0,
          defi: 0,
          effortsAFaire: 0,
          force: 0,
          total: 0
        })
      }

      const taskStats = taskStatsMap.get(task.taskId)!
      const evalRangeName =
        diagnosticModel.value === Values.DIAG_START
          ? task.diagEvalRangeName
          : task.diagEndEvalRangeName

      // Count evaluation using shared utility logic
      const singleScore = [{ evalRangeName }]
      const scoreStats = countEvaluationsByRange(singleScore)

      taskStats.defi += scoreStats.defi
      taskStats.effortsAFaire += scoreStats.effortsAFaire
      taskStats.force += scoreStats.force
      taskStats.notStarted += scoreStats.notStarted
      taskStats.total += 1
    })
  })

  // Build chart labels and data
  taskStatsMap.forEach((stats, taskId) => {
    const taskTitle = data[0]?.tasks?.find((task) => task.taskId === taskId)?.taskTitle
    const taskLabel = taskTitle || `Tâche ${taskId}`

    chartData.labels.push(taskLabel)
    chartData.datasets[0].data.push(stats.defi)
    chartData.datasets[1].data.push(stats.effortsAFaire)
    chartData.datasets[2].data.push(stats.force)
  })

  return chartData
})

// Check if chart has meaningful data to display
const hasChartData = computed(() => {
  const data = transformDataForChart.value
  return (
    data.labels.length > 0 &&
    data.datasets.some((dataset) => dataset.data.some((value) => value > 0))
  )
})

// Configuration du graphique avec meilleure expérience utilisateur
const chartOptions: ChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'right',
      labels: {
        color: '#495057',
        font: {
          size: 14
        }
      }
    },
    tooltip: {
      enabled: true,
      callbacks: {
        // Format tooltips for clarity
        label: (context: any) => {
          const label = context.dataset.label || ''
          const value = context.parsed?.y || 0
          return `${label}: ${value} élève${value > 1 ? 's' : ''}`
        }
      }
    },
    datalabels: {
      anchor: 'end',
      align: 'end',
      color: '#000',
      font: {
        size: 12,
        weight: 'bold'
      },
      // Only display values if > 0
      formatter: (value: any) => (value > 0 ? value.toString() : ''),
      display: (context: any) => (context.dataset.data[context.dataIndex] as number) > 0
    }
  },
  scales: {
    x: {
      ticks: {
        color: '#495057',
        font: {
          size: 14
        },
        callback: function (value: any) {
          const label = this.getLabelForValue(value)
          // Truncate long labels
          return label.length > 20 ? label.substring(0, 17) + '...' : label
        }
      },
      grid: {
        display: false
      }
    },
    y: {
      beginAtZero: true,
      max: (context: any) => {
        const maxValue = Math.max(...context.chart.data.datasets.flatMap((d: any) => d.data))
        // Add some space above the maximum value
        return Math.ceil(maxValue * 1.1)
      },
      ticks: {
        color: '#495057',
        font: {
          size: 14
        },
        // Adjust intervals based on max value
        stepSize: (context: any) => {
          const max = Math.max(...context.chart.data.datasets.flatMap((d: any) => d.data))
          return max <= 5 ? 1 : max <= 20 ? 2 : 5
        }
      },
      grid: {
        color: '#E5E5E5'
      }
    }
  },
  layout: {
    padding: {
      left: 10,
      right: 10,
      top: 10,
      bottom: 10
    }
  }
}

// Determine loading state
const isLoading = computed(() => {
  return loading.value || dashboardClassroomResultStoreLoading.value.fetchClassroomDiagnosticResults
})

// Determine empty state
const isEmpty = computed(() => {
  return (
    !isLoading.value &&
    (!props.diagnosticResults || props.diagnosticResults.length === 0 || !hasChartData.value)
  )
})
</script>

<template>
  <c-base-block
    title="Diagnostic"
    toggleable
    class="w-full"
  >
    <template #default>
      <!-- Affichage en cas d'erreur -->
      <p-message v-if="error" severity="error" class="mb-4 w-full">
        {{ error }}
      </p-message>

      <!-- Affichage en cas de chargement -->
      <div v-else-if="isLoading" class="flex items-center justify-center p-8">
        <p-progress-spinner
          style="width: 50px; height: 50px"
          strokeWidth="5"
          fill="#EEEEEE"
          animationDuration=".5s"
        />
      </div>

      <!-- Cas où il n'y a pas de données -->
      <div
        v-else-if="isEmpty"
        class="border-surface-300 col-span-10 flex flex-col gap-6 rounded-2xl border p-6"
      >
        <div class="flex gap-4 justify-items-start items-center">
          <p-select-button
            name="diagnosticSelection"
            :options="diagnosticOptions"
            v-model="diagnosticModel"
            option-label="name"
            option-value="value"
          />
        </div>
        <div class="flex flex-col items-center justify-center rounded-md bg-gray-50 p-6">
          <i class="pi pi-chart-bar text-surface-500 dark:text-surface-300 mb-4 text-4xl"></i>
          <h3 class="mt-0 mb-2">Aucune donnée disponible</h3>
          <p class="text-surface-700 dark:text-surface-100 text-center">
            Aucun résultat de diagnostic n'est disponible pour ce groupe actuellement.
          </p>
        </div>
      </div>

      <!-- Contenu principal -->
      <div v-else class="border-surface-300 col-span-10 flex flex-col gap-6 rounded-2xl border p-6">
        <h4 class="text-lg font-medium">Répartition des élèves par intervalles de scores</h4>
        <div class="flex gap-4">
          <p-select-button
            name="diagnosticSelection"
            class="w-fit"
            :options="diagnosticOptions"
            v-model="diagnosticModel"
            option-label="name"
            option-value="value"
          />
          <p-button class="text-xs font-bold w-fit" severity="primary" text @click="openDiagnostic()" icon="pi pi-external-link" icon-pos="right">
            Accéder au {{ diagnosticOptions.find((o) => o.value === diagnosticModel)?.name }}
          </p-button>
        </div>
        <div class="w-full">
          <p-chart
            type="bar"
            :data="transformDataForChart"
            :options="chartOptions"
            class="w-full"
          />
        </div>

        <c-dashboard-classroom-results-diagnostic-table
          :data-source="[]"
          :teachers-options="teachersOptions"
          :is-loading="isLoading"
          :type="diagnosticModel"
        />
      </div>
    </template>
  </c-base-block>
</template>
